// Every Figma value the dashboard's look uses (047 D6), read from the file
// Kh0KqgyVvWjtwQK8TW17Zn ("🛡️ Admin · Mizan"): light mode on 2026-09-14, dark mode on 2026-10-04
// from the dark copies of the Users, Teachers, Sessions, Financial, Governance and Notifications
// sections (047 D111). A plain module with no imports, so the theme, the kit and the Jest-tested
// helpers can all read it.
//
// `tokenValues` holds the colours of each mode. `tokens`, which every page imports, has the same
// shape with each colour as a CSS variable (`var(--mz-surface-default)`), so one attribute on
// <html>, set by MUI, switches every page at once (047 D106). The theme declares the variables
// for both modes (theme.js).
const light = {
  // `hover` and `on`: the chosen view chip and its count (Saved views 10620:2423). `border`: a
  // brand outline, focus ring or tab line (button/primary/border, 047 P135).
  brand: { primary: "#0b59cb", subtle: "#eef4fe", text: "#083c86", hover: "#0a4aa8", on: "#ffffff", border: "#0b59cb" },
  text: { primary: "#010b19", secondary: "#51575f", tertiary: "#676d75", disabled: "#7c828b", link: "#0a4aa8", inverse: "#ffffff" },
  // `brand`: a brand-blue icon (icon/brand, 047 P135).
  icon: { default: "#2c333d", secondary: "#727880", tertiary: "#c3c7cd", brand: "#0b59cb" },
  border: { default: "#ebecee", strong: "#dddfe3", subtle: "#f4f5f6" },
  divider: "#e4e5e9",
  // `page`: the main area behind the cards (surface/container-high), the same grey as `muted`
  // in light but not in dark (047 P117). `inverse`: the Reports map's tooltip (044 D90).
  surface: { default: "#ffffff", sunken: "#fafafb", muted: "#f4f5f6", page: "#f4f5f6", inverse: "#1d2632" },
  hover: "#fafafb",
  buttonBorder: "#3a4048",
  // Status tones (Session status dictionary 10554:3): `main` is the dot, `ink` the text on
  // `bg`, `border` the pill's outline.
  success: { main: "#04805a", ink: "#03674a", bg: "#e7f6f0", border: "#9bdcc3" },
  warning: { main: "#a6670a", ink: "#915b09", bg: "#fef4e6", border: "#facf8c" },
  error: { main: "#c33b3b", ink: "#a32c2c", bg: "#fdecec", border: "#f4aeae" },
  info: { main: "#076784", ink: "#076784", bg: "#e6f6fb", border: "#8fd6ec" },
  neutral: { main: "#010b19", ink: "#676d75", bg: "#f4f5f6", border: "#7c828b" },
  // The notification type tile's accent (brand/accent, text/accent-1).
  accent: { bg: "#f3edfe", ink: "#6a28d6" },
  // The notification preview's lock screen (ComposeParts): white words and an opaque mask over a
  // photo, so the same in both modes (047 P131).
  phone: { ink: "#ffffff", mask: "#000000" },
  // The tutor page's charts (Teacher overview 10982:85741, 10982:85773; 047 step 4): the donut's
  // tones, the ring's track and seams, the centre label, and the gauge's gradient stops.
  chart: {
    teal: "#0b7a70",
    amber: "#d4850a",
    red: "#a32c2c",
    // Reports' Session status ring (Figma "Home", 11529:96769; 044 slice 8 R8).
    green: "#04805a",
    blue: "#0b59cb",
    crimson: "#c33b3b",
    track: "#c3c7cd",
    // Reports step 2 (044 D85–D88): the No-show slice, Female in Users by gender (#dc5050 at
    // 40% on white, as Figma draws it), and the Revenue chart's gridlines.
    ochre: "#a6670a",
    rose: "#f1b9b9",
    grid: "#eff1f2",
    seam: "#f4f5f6",
    label: "#737373",
    // Reports' Users by country (044 D90, Figma 11541:96657): the map key's five blues, fewest to
    // most (surface/brand-1, chart/seq-1 … seq-4), a country with none, and the map's well.
    seq: ["#d6e4fc", "#7fa8f2", "#3d79e6", "#0b59cb", "#083c86"],
    seqEmpty: "#eff1f2",
    well: "#fafafb",
    // Users by gender (D93, 11632:175509): Female, and the Child learners dot (chart/seq-2).
    violet: "#9a66f0",
    sky: "#3d79e6",
    // Reports' subject chart (044 D126; Figma "Income over time", 11661:146231):
    // chart/series-quran, chart/series-arabic, chart/series-islamic.
    series: { quran: "#0d9488", arabic: "#0b59cb", islamic: "#9a66f0" },
    gauge: [
      { offset: 0, color: "#c33b3b" },
      { offset: 0.16345, color: "#d4850a" },
      { offset: 0.528848, color: "#0d9488" },
      { offset: 1, color: "#087598" },
    ],
  },
  // The rating star (10982:85773) and the solid teal Verify button (10982:85810).
  star: "#f59e0b",
  secondary: "#0b7a70",
  // MUI's secondary colour, kept as it was: two pages not redrawn yet colour a gender chip with it.
  legacySecondary: "#dc004e",
  // `raised`: a chosen toggle and an old page's card (047 D106).
  shadow: {
    menu: "0px 2px 6px -2px rgba(15,23,41,0.06), 0px 12px 24px -6px rgba(15,23,41,0.12)",
    panel: "0px 2px 8px rgba(0,0,0,0.08), 0px 24px 64px rgba(0,0,0,0.18)",
    raised: "0 1px 2px rgba(0,0,0,0.08)",
  },
  // Avatar · Initial colour pairs (avatar/NN/bg, avatar/NN/fg). Pairs 09, 14 and 15 were in
  // none of the nodes read.
  avatar: [
    { bg: "#fcafc1", fg: "#9d1334" }, // 01
    { bg: "#fbb1a9", fg: "#992013" }, // 02
    { bg: "#fab581", fg: "#7c3f0f" }, // 03
    { bg: "#f9d676", fg: "#755a0e" }, // 04
    { bg: "#c0f976", fg: "#46710e" }, // 05
    { bg: "#76f999", fg: "#0e7129" }, // 06
    { bg: "#76f9cd", fg: "#0e7150" }, // 07
    { bg: "#76f9f9", fg: "#0e7070" }, // 08
    { bg: "#77cef9", fg: "#0f5477" }, // 10
    { bg: "#9ec7fb", fg: "#134e9b" }, // 11
    { bg: "#bdbefc", fg: "#2a2de5" }, // 12
    { bg: "#d9b5fc", fg: "#6e18c3" }, // 13
  ],
};

// Figma's dark values for the same keys (047 D111). Where Figma has no dark design, or one key
// stands for two Figma variables that part in dark, plan-step7-dark-mode.md records the choice
// (047 P116–P123).
const dark = {
  // Figma's brighter blue for brand lines and icons: #0b59cb is too dim on a dark card (P135).
  brand: { primary: "#0b59cb", subtle: "#04203f", text: "#7fa8f2", hover: "#3d79e6", on: "#ffffff", border: "#3d79e6" },
  text: { primary: "#fafafb", secondary: "#dddfe3", tertiary: "#a6abb3", disabled: "#b5b9c0", link: "#7fa8f2", inverse: "#010b19" },
  icon: { default: "#dddfe3", secondary: "#c3c7cd", tertiary: "#a6abb3", brand: "#3d79e6" },
  border: { default: "#3a4048", strong: "#676d75", subtle: "#3a4048" },
  divider: "#2c333d",
  // Cards and the shell are surface/default, as the Money and Governance frames draw them (P116);
  // a panel inside a card is flat with it, as Figma's dark table heads are (P118).
  surface: { default: "#1d2632", sunken: "#1d2632", muted: "#3a4048", page: "#2c333d", inverse: "#fafafb" },
  hover: "#2c333d",
  buttonBorder: "#ebecee",
  // A pill's outline is its tone's light dot colour (P120).
  success: { main: "#2fae81", ink: "#9bdcc3", bg: "#013226", border: "#04805a" },
  warning: { main: "#f6a82e", ink: "#facf8c", bg: "#533405", border: "#a6670a" },
  error: { main: "#e56a6a", ink: "#f4aeae", bg: "#5c1717", border: "#c33b3b" },
  info: { main: "#22a9d3", ink: "#8fd6ec", bg: "#033543", border: "#076784" },
  neutral: { main: "#fafafb", ink: "#dddfe3", bg: "#3a4048", border: "#676d75" },
  accent: { bg: "#331168", ink: "#cfb9fb" },
  phone: light.phone,
  // The tutor page's donut keeps Figma's dark tones (teal brighter; amber, red and the track as
  // they are). The Reports charts have no dark design: the nearest Figma dark colours (P121).
  chart: {
    teal: "#27aba0",
    amber: "#d4850a",
    red: "#a32c2c",
    green: "#2fae81",
    blue: "#3d79e6",
    crimson: "#e56a6a",
    track: "#c3c7cd",
    ochre: "#f6a82e",
    // #dc5050 at 40% on the dark card.
    rose: "#69373e",
    grid: "#2c333d",
    // A seam is the card showing between two slices.
    seam: "#1d2632",
    label: "#a6abb3",
    // Fewest to most still reads dark to bright: the key turns round on a dark card.
    seq: ["#04203f", "#083c86", "#0b59cb", "#3d79e6", "#7fa8f2"],
    seqEmpty: "#2c333d",
    well: "#010b19",
    violet: "#9a66f0",
    sky: "#3d79e6",
    // The nearest Figma dark colours (047 P121): the dark teal and blue; the violet as it is.
    series: { quran: "#27aba0", arabic: "#3d79e6", islamic: "#9a66f0" },
    gauge: [
      { offset: 0, color: "#c33b3b" },
      { offset: 0.16345, color: "#d4850a" },
      { offset: 0.528848, color: "#0d9488" },
      { offset: 1, color: "#087598" },
    ],
  },
  star: "#f59e0b",
  secondary: "#0b7a70",
  legacySecondary: "#dc004e",
  // Black at a higher opacity, so a menu still lifts off a dark page (P122).
  shadow: {
    menu: "0px 2px 6px -2px rgba(0,0,0,0.4), 0px 12px 24px -6px rgba(0,0,0,0.5)",
    panel: "0px 2px 8px rgba(0,0,0,0.4), 0px 24px 64px rgba(0,0,0,0.6)",
    raised: "0 1px 2px rgba(0,0,0,0.5)",
  },
  // Figma's dark avatars swap each pair: the deep colour fills, the pale one writes.
  avatar: light.avatar.map(({ bg, fg }) => ({ bg: fg, fg: bg })),
};

export const tokenValues = { light, dark };

// "chart.seq.0" → "--mz-chart-seq-0"; a camelCase key becomes kebab-case.
const cssName = (path) => `--mz-${path.map((p) => String(p).replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)).join("-")}`;

const mapLeaves = (node, fn, path = []) => {
  if (Array.isArray(node)) return node.map((v, i) => mapLeaves(v, fn, [...path, i]));
  if (node && typeof node === "object") {
    return Object.fromEntries(Object.entries(node).map(([k, v]) => [k, mapLeaves(v, fn, [...path, k])]));
  }
  return fn(node, path);
};

// One mode's CSS variables: { "--mz-brand-primary": "#0b59cb", … }. A number (a gauge stop's
// offset) is not a colour, so it gets no variable.
export const cssVariables = (values) => {
  const vars = {};
  mapLeaves(values, (v, path) => {
    if (typeof v === "string") vars[cssName(path)] = v;
    return v;
  });
  return vars;
};

export const tokens = mapLeaves(light, (v, path) => (typeof v === "string" ? `var(${cssName(path)})` : v));

// Keyboard focus on a raw ButtonBase, which MUI draws with no outline: an inset ring, so a
// parent's overflow never clips it (047: sidebar, top bar, search rows, child cards).
export const focusRing = { "&.Mui-focusVisible": { outline: `2px solid ${tokens.brand.border}`, outlineOffset: "-2px" } };

// The list pages' head buttons (Transactions' period and Export, Reports' Export — slice 7
// R18, slice 8 R7).
export const headButtonSx = {
  minWidth: 0,
  padding: "11px 18px 11px 14px",
  gap: "8px",
  borderRadius: "10px",
  border: `1px solid ${tokens.border.default}`,
  bgcolor: tokens.surface.default,
  color: tokens.text.primary,
  fontSize: 14,
  lineHeight: "20px",
  fontWeight: 600,
  textTransform: "none",
  "&:hover": { bgcolor: tokens.hover },
  "&.Mui-disabled": { color: tokens.text.disabled },
  ...focusRing,
};

// Text for screen readers only (the Tutors list's h1, P59; a new-tab link's "(opens in a new
// tab)", 047 D80).
export const srOnly = {
  position: "absolute",
  width: "1px",
  height: "1px",
  margin: "-1px",
  padding: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

// Avatar · Initial colour pairs, as CSS variables, so a dark page swaps them (see `avatar` above).
export const AVATAR_PAIRS = tokens.avatar;
